"use client"

import { useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"

type PageItem = number | "ellipsis-start" | "ellipsis-end"

export interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: readonly number[]
  disabled?: boolean
  pageNeighbours?: 0 | 1 | 2
  className?: string
  infoClassName?: string
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  disabled = false,
  pageNeighbours = 1,
  className,
  infoClassName,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)))
  const currentPage = clamp(page, 1, totalPages)

  const pageItems = useMemo<PageItem[]>(() => {
    const neighbours = clamp(pageNeighbours, 0, 2)
    const start = Math.max(1, currentPage - neighbours)
    const end = Math.min(totalPages, currentPage + neighbours)
    const items: PageItem[] = []

    if (start > 1) {
      items.push(1)
      if (start > 2) {
        items.push("ellipsis-start")
      }
    }

    for (let p = start; p <= end; p += 1) {
      items.push(p)
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push("ellipsis-end")
      }
      items.push(totalPages)
    }

    return items
  }, [currentPage, totalPages, pageNeighbours])

  const rangeStart = total === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const rangeEnd = total === 0 ? 0 : Math.min(total, rangeStart + pageSize - 1)
  const canGoPrev = !disabled && currentPage > 1
  const canGoNext = !disabled && currentPage < totalPages

  const handlePageChange = (nextPage: number) => {
    const safePage = clamp(nextPage, 1, totalPages)
    if (!disabled && safePage !== currentPage) {
      onPageChange(safePage)
    }
  }

  const handlePageSizeChange = (value: string) => {
    const nextSize = Number.parseInt(value, 10)
    if (Number.isFinite(nextSize) && nextSize > 0 && onPageSizeChange && !disabled) {
      onPageSizeChange(nextSize)
    }
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 border-t border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between",
        className,
      )}
    >
      <div className={cn("flex items-center gap-2 text-sm text-gray-600", infoClassName)}>
        <span>Showing</span>
        <span className="font-medium text-gray-900">
          {rangeStart}-{rangeEnd}
        </span>
        <span>of</span>
        <span className="font-medium text-gray-900">{total}</span>
      </div>

      <div className="flex flex-col-reverse gap-3 md:flex-row md:items-center md:gap-4">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange} disabled={disabled}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            disabled={!canGoPrev}
            onClick={() => handlePageChange(1)}
            aria-label="Go to first page"
          >
            {"<<"}
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={!canGoPrev}
            onClick={() => handlePageChange(currentPage - 1)}
            aria-label="Go to previous page"
          >
            {"<"}
          </Button>
          {pageItems.map((item, idx) =>
            typeof item === "number" ? (
              <Button
                key={`page-${item}-${idx}`}
                variant={item === currentPage ? "default" : "outline"}
                size="icon"
                className={item === currentPage ? "bg-blue-600 text-white hover:bg-blue-600" : undefined}
                onClick={() => handlePageChange(item)}
                disabled={disabled}
                aria-current={item === currentPage ? "page" : undefined}
              >
                {item}
              </Button>
            ) : (
              <span key={`${item}-${idx}`} className="px-2 text-sm text-gray-400">
                ...
              </span>
            ),
          )}
          <Button
            variant="outline"
            size="icon"
            disabled={!canGoNext}
            onClick={() => handlePageChange(currentPage + 1)}
            aria-label="Go to next page"
          >
            {">"}
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={!canGoNext}
            onClick={() => handlePageChange(totalPages)}
            aria-label="Go to last page"
          >
            {">>"}
          </Button>
        </div>
      </div>
    </div>
  )
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}
